import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  Inbox,
  MessageCircle,
  Mail,
  Trash2,
  Eye,
  Phone,
  MapPin,
  Building2,
  Calendar,
} from "lucide-react";
import { useLeads, type Lead, type LeadStatus } from "@/hooks/useLeads";

const STATUS_LABEL: Record<LeadStatus, string> = {
  novo: "Novo",
  em_contato: "Em contato",
  convertido: "Convertido",
  descartado: "Descartado",
};

const STATUS_CLASS: Record<LeadStatus, string> = {
  novo: "bg-[#00aaff]/15 text-[#00aaff] border-[#00aaff]/30",
  em_contato: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  convertido: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  descartado: "bg-muted text-muted-foreground border-border",
};

function formatDateBR(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function whatsappLink(num: string | null) {
  if (!num) return null;
  const digits = num.replace(/\D/g, "");
  if (digits.length < 10) return null;
  const withCountry = digits.startsWith("55") ? digits : `55${digits}`;
  return `https://wa.me/${withCountry}`;
}

export default function AdminLeadsPage() {
  const { leads, loading, updateLeadStatus, updateLeadNotes, deleteLead } =
    useLeads();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [periodFilter, setPeriodFilter] = useState<string>("all");
  const [selected, setSelected] = useState<Lead | null>(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<Lead | null>(null);

  const counters = useMemo(() => {
    return {
      total: leads.length,
      novo: leads.filter((l) => l.status === "novo").length,
      em_contato: leads.filter((l) => l.status === "em_contato").length,
      convertido: leads.filter((l) => l.status === "convertido").length,
    };
  }, [leads]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const now = Date.now();
    const periodMs: Record<string, number> = {
      today: 24 * 60 * 60 * 1000,
      "7d": 7 * 24 * 60 * 60 * 1000,
      "30d": 30 * 24 * 60 * 60 * 1000,
    };
    return leads.filter((l) => {
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (periodFilter !== "all") {
        const ms = periodMs[periodFilter];
        if (ms && now - new Date(l.created_at).getTime() > ms) return false;
      }
      if (!term) return true;
      const hay = [
        l.nome,
        l.email,
        l.whatsapp,
        l.cidade,
        l.estabelecimento,
        l.tipo,
        l.mensagem,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });
  }, [leads, search, statusFilter, periodFilter]);

  const openDetails = (lead: Lead) => {
    setSelected(lead);
    setNotesDraft(lead.notes || "");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#00aaff]/10 border border-[#00aaff]/30">
          <Inbox className="h-6 w-6 text-[#00aaff]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Leads</h1>
          <p className="text-sm text-muted-foreground">
            Mensagens recebidas pelo formulário de contato do site
          </p>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Total</div>
            <div className="text-2xl font-bold">{counters.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Novos</div>
            <div className="text-2xl font-bold text-[#00aaff]">{counters.novo}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Em contato</div>
            <div className="text-2xl font-bold text-yellow-400">
              {counters.em_contato}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Convertidos</div>
            <div className="text-2xl font-bold text-emerald-400">
              {counters.convertido}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, email, WhatsApp, cidade..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-[180px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              <SelectItem value="novo">Novos</SelectItem>
              <SelectItem value="em_contato">Em contato</SelectItem>
              <SelectItem value="convertido">Convertidos</SelectItem>
              <SelectItem value="descartado">Descartados</SelectItem>
            </SelectContent>
          </Select>
          <Select value={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="w-full md:w-[180px]">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo o período</SelectItem>
              <SelectItem value="today">Últimas 24h</SelectItem>
              <SelectItem value="7d">Últimos 7 dias</SelectItem>
              <SelectItem value="30d">Últimos 30 dias</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead className="hidden md:table-cell">Estabelecimento</TableHead>
                <TableHead className="hidden lg:table-cell">Mensagem</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    <Inbox className="h-10 w-10 mx-auto mb-2 opacity-40" />
                    Nenhum lead encontrado
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((lead) => {
                  const wa = whatsappLink(lead.whatsapp);
                  return (
                    <TableRow key={lead.id} className="cursor-pointer" onClick={() => openDetails(lead)}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {formatDateBR(lead.created_at)}
                      </TableCell>
                      <TableCell className="font-medium">{lead.nome}</TableCell>
                      <TableCell>
                        <div className="flex flex-col text-xs gap-0.5">
                          {lead.whatsapp && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {lead.whatsapp}
                            </span>
                          )}
                          {lead.email && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Mail className="h-3 w-3" /> {lead.email}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm">
                        {lead.estabelecimento || "-"}
                        {lead.cidade && (
                          <div className="text-xs text-muted-foreground">
                            {lead.cidade}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell max-w-xs truncate text-sm text-muted-foreground">
                        {lead.mensagem || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={STATUS_CLASS[lead.status]}>
                          {STATUS_LABEL[lead.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          {wa && (
                            <Button
                              size="icon"
                              variant="ghost"
                              asChild
                              title="Abrir WhatsApp"
                            >
                              <a href={wa} target="_blank" rel="noreferrer">
                                <MessageCircle className="h-4 w-4 text-emerald-400" />
                              </a>
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openDetails(lead)}
                            title="Ver detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Details Sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle>{selected.nome}</SheetTitle>
                <SheetDescription className="flex items-center gap-1 text-xs">
                  <Calendar className="h-3 w-3" />
                  Recebido em {formatDateBR(selected.created_at)}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                {/* Status */}
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select
                    value={selected.status}
                    onValueChange={(v) =>
                      updateLeadStatus(selected.id, v as LeadStatus).then(() =>
                        setSelected({ ...selected, status: v as LeadStatus })
                      )
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="novo">Novo</SelectItem>
                      <SelectItem value="em_contato">Em contato</SelectItem>
                      <SelectItem value="convertido">Convertido</SelectItem>
                      <SelectItem value="descartado">Descartado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Quick actions */}
                <div className="flex flex-wrap gap-2">
                  {whatsappLink(selected.whatsapp) && (
                    <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                      <a href={whatsappLink(selected.whatsapp)!} target="_blank" rel="noreferrer">
                        <MessageCircle className="h-4 w-4 mr-2" />
                        WhatsApp
                      </a>
                    </Button>
                  )}
                  {selected.email && (
                    <Button asChild variant="outline">
                      <a href={`mailto:${selected.email}`}>
                        <Mail className="h-4 w-4 mr-2" />
                        Email
                      </a>
                    </Button>
                  )}
                </div>

                {/* Info */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selected.whatsapp && (
                    <div>
                      <Label className="text-xs text-muted-foreground">WhatsApp</Label>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Phone className="h-3.5 w-3.5" /> {selected.whatsapp}
                      </div>
                    </div>
                  )}
                  {selected.email && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Email</Label>
                      <div className="flex items-center gap-1.5 mt-1 truncate">
                        <Mail className="h-3.5 w-3.5 shrink-0" />{" "}
                        <span className="truncate">{selected.email}</span>
                      </div>
                    </div>
                  )}
                  {selected.cidade && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Cidade</Label>
                      <div className="flex items-center gap-1.5 mt-1">
                        <MapPin className="h-3.5 w-3.5" /> {selected.cidade}
                      </div>
                    </div>
                  )}
                  {selected.estabelecimento && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Estabelecimento</Label>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Building2 className="h-3.5 w-3.5" /> {selected.estabelecimento}
                      </div>
                    </div>
                  )}
                  {selected.tipo && (
                    <div className="col-span-2">
                      <Label className="text-xs text-muted-foreground">Tipo</Label>
                      <div className="mt-1">{selected.tipo}</div>
                    </div>
                  )}
                </div>

                {/* Message */}
                {selected.mensagem && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Mensagem</Label>
                    <div className="mt-1 p-3 rounded-md bg-muted/40 border border-border text-sm whitespace-pre-wrap">
                      {selected.mensagem}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <Label className="text-xs">Anotações internas</Label>
                  <Textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder="Adicione observações sobre este contato..."
                    rows={4}
                    className="mt-1"
                  />
                  <Button
                    size="sm"
                    className="mt-2"
                    onClick={() => updateLeadNotes(selected.id, notesDraft)}
                    disabled={notesDraft === (selected.notes || "")}
                  >
                    Salvar anotações
                  </Button>
                </div>

                {/* Delete */}
                <div className="pt-4 border-t border-border">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setConfirmDelete(selected)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir lead
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir lead?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O lead "{confirmDelete?.nome}" será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (confirmDelete) {
                  const ok = await deleteLead(confirmDelete.id);
                  if (ok) {
                    setConfirmDelete(null);
                    setSelected(null);
                  }
                }
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
